// src/review/canvas/navigation/RiskNavigator.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Stack, Card, Text, ScrollArea, UnstyledButton, Group, Badge, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconPlus, IconShield } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useReview } from '../../context/ReviewContext';
import AddRiskModal from './AddRiskModal';
import humanizeAreaKey from '../../../utils/humanizeAreaKey';

/**
 * RiskNavigator - Left sidebar for navigating through risks
 * Shows all risks grouped by area with review status
 * Supports adding manual risks via template-based modal
 */
function RiskNavigator({ risksByArea, currentRisk, onRiskSelect, templateData, onAddRisk }) {
  const { modelId, goToStep } = useReview();

  // Modal state
  const [addRiskModalOpen, setAddRiskModalOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  // Get reviewed risks from Redux
  const reviewState = useSelector((state) =>
    state.reviews.byModel[modelId] || {}
  );
  const reviewedRisks = reviewState.risksReviewed || [];

  // Get risk sources and causes for an area from template
  const getRiskSourcesAndCauses = useMemo(() => {
    if (!templateData?.taxonomy?.riskAreas) return () => [];

    return (areaKey) => {
      const areaData = templateData.taxonomy.riskAreas[areaKey];
      if (!areaData) return [];

      const riskSources = areaData.riskSources || [];

      // Transform risk sources into selectable options
      const sourceOptions = riskSources.map(source => ({
        value: source.name,
        label: source.name,
        type: 'source'
      }));

      // Extract causes from each source and flatten
      const causeOptions = riskSources.flatMap(source => {
        if (!source.causes) return [];
        return Object.entries(source.causes).map(([causeKey, causeData]) => ({
          value: causeKey,
          label: causeData.description || causeKey,
          type: 'cause',
          parentSource: source.name
        }));
      });

      return [...sourceOptions, ...causeOptions];
    };
  }, [templateData]);

  // Handlers
  const handleOpenAddRisk = (areaKey, areaLabel) => {
    setSelectedArea({ key: areaKey, label: areaLabel });
    setAddRiskModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddRiskModalOpen(false);
    setSelectedArea(null);
  };

  const handleSubmitRisk = async (data) => {
    await onAddRisk(data);
    handleCloseModal();
  };

  return (
    <Card withBorder p="md" style={{ height: '100%' }}>
      <Stack gap="md">
        {/* Header with RISK AREAS title and Skip to Security icon */}
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600} c="dimmed">RISK AREAS</Text>
          <Tooltip label="Skip to Security">
            <ActionIcon
              variant="light"
              color="blue"
              size="md"
              onClick={() => goToStep('security')}
            >
              <IconShield size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <ScrollArea style={{ height: 'calc(100vh - 200px)' }}>
          <Stack gap="lg">
            {Object.entries(risksByArea).map(([areaName, risks]) => (
              <Stack key={areaName} gap="xs">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                  {humanizeAreaKey(areaName)}
                </Text>

                {risks.map((risk) => {
                  const isActive = currentRisk?.riskId === risk.riskId;
                  const isReviewed = reviewedRisks.includes(risk.riskId);

                  return (
                    <UnstyledButton
                      key={risk.riskId}
                      onClick={() => onRiskSelect(risk)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        backgroundColor: isActive ? '#e7f5ff' : 'transparent',
                        border: isActive ? '1px solid #339af0' : '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Text
                          size="sm"
                          fw={isActive ? 600 : 400}
                          c={isActive ? 'blue' : 'dark'}
                          lineClamp={2}
                          style={{ flex: 1 }}
                        >
                          {risk.riskName || risk.riskId}
                        </Text>

                        {isReviewed && (
                          <Badge
                            size="xs"
                            variant="filled"
                            color="green"
                            leftSection={<IconCheck size={10} />}
                            style={{ minWidth: 60 }}
                          >
                            Reviewed
                          </Badge>
                        )}
                      </Group>
                    </UnstyledButton>
                  );
                })}

                {/* Add Risk button */}
                {onAddRisk && (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => handleOpenAddRisk(areaName, areaName)}
                    fullWidth
                    mt="xs"
                  >
                    Add Risk
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </ScrollArea>
      </Stack>

      {/* Add Risk Modal */}
      {selectedArea && (
        <AddRiskModal
          opened={addRiskModalOpen}
          onClose={handleCloseModal}
          areaKey={selectedArea.key}
          areaLabel={selectedArea.label}
          availableCategories={getRiskSourcesAndCauses(selectedArea.key)}
          onSubmit={handleSubmitRisk}
        />
      )}
    </Card>
  );
}

RiskNavigator.propTypes = {
  risksByArea: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        riskId: PropTypes.string.isRequired,
        riskName: PropTypes.string,
        areaName: PropTypes.string,
      })
    )
  ).isRequired,
  currentRisk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
    riskName: PropTypes.string,
  }),
  onRiskSelect: PropTypes.func.isRequired,
  templateData: PropTypes.object,
  onAddRisk: PropTypes.func,
};

RiskNavigator.defaultProps = {
  currentRisk: null,
  templateData: null,
  onAddRisk: null,
};

export default RiskNavigator;
